import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import FieldTextInput from '../FieldTextInput/FieldTextInput';
import IconCard from '../SavedCardDetails/IconCard/IconCard';

import css from './FieldPasswordField.module.css';

/**
 * A component that renders a password field with a show/hide password toggle icon.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.id] - Field id
 * @param {string} [props.name='password'] - Field name in React Final Form
 * @param {string} [props.label] - Field label
 * @param {string} [props.placeholder] - Input placeholder
 * @param {Function} [props.validate] - Final form validator
 * @param {string} [props.className] - Extra class name for wrapper
 * @param {string} [props.rootClassName] - Root class name for FieldTextInput
 * @param {string} [props.inputClassName] - Extra class name for input element
 * @param {string} [props.autoComplete='current-password'] - AutoComplete attribute
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {string} [props.customErrorText] - Custom error message text
 * @returns {JSX.Element}
 */
const FieldPasswordField = props => {
  const {
    id,
    name = 'password',
    label,
    placeholder,
    validate,
    className,
    rootClassName,
    inputClassName,
    autoComplete = 'current-password',
    disabled,
    customErrorText,
    ...rest
  } = props;

  const intl = useIntl();
  const [isVisible, setIsVisible] = useState(false);

  const toggleLabel = intl
    ? intl.formatMessage({
        id: isVisible ? 'LoginForm.hidePassword' : 'LoginForm.showPassword',
        defaultMessage: isVisible ? 'Hide password' : 'Show password',
      })
    : isVisible
    ? 'Hide password'
    : 'Show password';

  return (
    <div className={classNames(css.passwordWrapper, className)}>
      <FieldTextInput
        type={isVisible ? 'text' : 'password'}
        id={id || name}
        name={name}
        autoComplete={autoComplete}
        label={label}
        placeholder={placeholder}
        validate={validate}
        disabled={disabled}
        customErrorText={customErrorText}
        className={inputClassName}
        rootClassName={rootClassName}
        {...rest}
      />
      <button
        type="button"
        className={classNames(css.passwordToggle, { [css.hasLabel]: !!label })}
        onClick={() => setIsVisible(prev => !prev)}
        aria-label={toggleLabel}
        aria-pressed={isVisible}
        title={toggleLabel}
      >
        <IconCard brand={isVisible ? 'eyeOff' : 'eye'} />
      </button>
    </div>
  );
};

export default FieldPasswordField;
